import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../ui/button";
import { RotateCw, Trophy } from "lucide-react";

const EMOJIS = ["🎯", "🚀", "🎨", "🎮", "💎", "🔥", "⚡", "🌟"]; // 8 pairs = 16 cards

function shuffle(arr) {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}

function buildDeck() {
    const pairs = EMOJIS.flatMap((e, i) => [
        { id: `${i}-a`, value: e, matched: false, flipped: false },
        { id: `${i}-b`, value: e, matched: false, flipped: false }
    ]);
    return shuffle(pairs);
}

export function GameMemory({ onScoreSubmit }) {
    const [cards, setCards] = useState(buildDeck);
    const [flipped, setFlipped] = useState([]); // indices currently flipped face-up
    const [moves, setMoves] = useState(0);
    const [best, setBest] = useState(() => parseInt(localStorage.getItem("crm_memory_best") || "0", 10));
    const startedAt = useRef(Date.now());
    const submitted = useRef(false);

    const allMatched = useMemo(() => cards.every((c) => c.matched), [cards]);

    useEffect(() => {
        if (allMatched && !submitted.current) {
            submitted.current = true;
            // Lower moves = better. Score = max(0, 5000 - moves*100)
            const score = Math.max(0, 5000 - moves * 100);
            if (moves < best || best === 0) {
                setBest(moves);
                localStorage.setItem("crm_memory_best", String(moves));
            }
            onScoreSubmit?.({ score, durationMs: Date.now() - startedAt.current });
        }
    }, [allMatched, moves, best, onScoreSubmit]);

    const handleFlip = (index) => {
        if (cards[index].matched || cards[index].flipped) return;
        if (flipped.length === 2) return;

        const newCards = cards.map((c, i) => i === index ? { ...c, flipped: true } : c);
        const newFlipped = [...flipped, index];
        setCards(newCards);
        setFlipped(newFlipped);

        if (newFlipped.length === 2) {
            setMoves((m) => m + 1);
            const [a, b] = newFlipped;
            if (newCards[a].value === newCards[b].value) {
                // Match
                setTimeout(() => {
                    setCards((cs) => cs.map((c, i) => (i === a || i === b) ? { ...c, matched: true } : c));
                    setFlipped([]);
                }, 350);
            } else {
                setTimeout(() => {
                    setCards((cs) => cs.map((c, i) => (i === a || i === b) ? { ...c, flipped: false } : c));
                    setFlipped([]);
                }, 800);
            }
        }
    };

    const reset = () => {
        setCards(buildDeck());
        setFlipped([]);
        setMoves(0);
        submitted.current = false;
        startedAt.current = Date.now();
    };

    return (
        <div className="flex flex-col items-center w-full">
            <div className="flex items-center justify-between w-full max-w-md mb-3">
                <div className="flex gap-2">
                    <ScoreBox label="Moves" value={moves} />
                    <ScoreBox label="Best" value={best || "-"} accent />
                </div>
                <Button size="sm" variant="outline" onClick={reset}><RotateCw className="h-3.5 w-3.5 mr-1" />New game</Button>
            </div>

            <div className="grid grid-cols-4 gap-2 w-full" style={{ maxWidth: 420 }}>
                {cards.map((c, i) => (
                    <div
                        key={c.id}
                        onClick={() => handleFlip(i)}
                        className={`memory-card ${c.flipped || c.matched ? "flipped" : ""} ${c.matched ? "matched" : ""}`}
                    >
                        <div className="memory-card-inner">
                            <div className="memory-card-face front">?</div>
                            <div className="memory-card-face back">{c.value}</div>
                        </div>
                    </div>
                ))}
            </div>

            {allMatched && (
                <div className="mt-4 px-4 py-3 bg-purple-50 border border-purple-200 rounded-lg text-center max-w-md">
                    <Trophy className="h-5 w-5 text-purple-600 inline mb-1" />
                    <div className="text-sm font-semibold text-purple-800">
                        Cleared in {moves} moves · {Math.round((Date.now() - startedAt.current) / 1000)}s
                    </div>
                    <Button size="sm" onClick={reset} className="mt-2 bg-purple-600 hover:bg-purple-700 text-white">Play again</Button>
                </div>
            )}
        </div>
    );
}

function ScoreBox({ label, value, accent }) {
    return (
        <div className={`px-3 py-1.5 rounded-md text-center ${accent ? "bg-amber-100 text-amber-800" : "bg-gray-100 text-gray-700"}`}>
            <div className="text-[10px] uppercase font-semibold tracking-wide opacity-70">{label}</div>
            <div className="text-base font-bold tabular-nums">{value}</div>
        </div>
    );
}
